import React, { FunctionComponent, useEffect, useState } from 'react'
import { reactular } from '@vendhq/reactular'
import { LoaderSpinner, Scrollable } from '@lightspeed/design-system-react'
import { useAppSelector } from '../../../state/hooks'
import {
  selectSaleDisplayStatus,
  selectSaleStatus,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { isQuote, shouldDisplaySaleNote } from '../../../functions/sale'
import {
  useRegisterManager,
  useTrainingModeManager,
  useVdEscapeStack,
} from '../../../ng-compat/hooks'
import { NoteError } from '../../shared/NoteInput'
import { SaleActionBar } from './SaleActionBar'
import { ConfirmationHeader } from './ConfirmationHeader'
import { PrintButtons } from './PrintButtons'
import { ConfirmationCustomer } from './ConfirmationCustomer'
import { ConfirmationNote } from './ConfirmationNote'
import { useConfirmationInit } from './useConfirmationInit'
import { useCloseSaleConfirmation } from './useCloseSaleConfirmation'
import { useCompleteTutorial } from './useCompleteTutorial'

export const ConfirmationPanel: FunctionComponent = () => {
  const status = useAppSelector(selectSaleStatus)
  const displayStatus = useAppSelector(selectSaleDisplayStatus)
  const register = useRegisterManager().getActiveRegister()
  const [saleNoteHasError, setSaleNoteHasError] = useState(false)
  const trainingModeManager = useTrainingModeManager()
  const hasInit = useConfirmationInit()
  const closeSaleConfirmation = useCloseSaleConfirmation()
  const completeTutorial = useCompleteTutorial()
  const escapeStack = useVdEscapeStack()

  const showSaleNote = shouldDisplaySaleNote(register, { displayStatus })

  useEffect(() => {
    const handleComplete = () => {
      if (trainingModeManager.trainingModeOnboardingState) {
        completeTutorial()
      } else {
        closeSaleConfirmation('escape')
      }
    }

    escapeStack.register(handleComplete)

    return () => {
      escapeStack.deregister(handleComplete)
    }
  }, [
    closeSaleConfirmation,
    completeTutorial,
    escapeStack,
    trainingModeManager.trainingModeOnboardingState,
  ])

  if (!hasInit) {
    return (
      <div className="vd-card payment-panel--action">
        <LoaderSpinner className="vd-mt10" />
      </div>
    )
  }

  return (
    <div className="vd-card payment-panel--action">
      <Scrollable monobrow={true} goatee={true}>
        <div className="payment-panel-content payment-panel-content--action">
          <div className="sale-confirmation-container">
            <ConfirmationHeader />
            {isQuote(status) ? null : <PrintButtons />}
            <ConfirmationCustomer />
            {showSaleNote ? (
              <ConfirmationNote
                onErrorsChange={(errors: NoteError[]) =>
                  setSaleNoteHasError(Boolean(errors.length))
                }
              />
            ) : null}
          </div>
        </div>
      </Scrollable>
      <SaleActionBar actionDisabled={saleNoteHasError} />
    </div>
  )
}

export const ReactConfirmationPanel = reactular(
  ConfirmationPanel,
  [],
  'reactWrapper'
)
